import {
  IconBulb,
  IconClipboardList,
  IconCoin,
  IconHome,
  IconPackage,
  IconShieldLock,
  IconUsers,
  IconVideo,
} from "@tabler/icons-react";
import {
  SidebarProvider,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenuItem,
  SidebarMenu,
  SidebarMenuButton,
} from "../Sidebar";
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router";
import { externalPaths, paths } from "@/App";
import CreditBalanceSidebar from "./CreditBalanceSidebar";
import { TeamSwitcher } from "../TeamSwitcher";
import { useCurrentTeamMembership } from "@/lib/hooks/team-hooks";
import { TeamPermissions } from "@repo/api/team-roles";
import { useGenerateCustomerPortalUrl } from "@/lib/queries/billing-queries";
import Toast from "../Toast";
import LoadingSpinner from "../LoadingSpinner";
import AddCreditsDialog from "@/features/Billing/AddCreditsDialog";
import { useState } from "react";
import { useTeamQuery } from "@/lib/queries/team-queries";
import { useIsFeatureEnabled } from "@/lib/feature-flags";
import { getEnv } from "@/lib/utils";
import OnboardingSurveyDialog from "@/features/OnboardingSurvey/OnboardingSurveyDialog";
import { useSelfQuery } from "@/lib/queries/user-queries";
import { AnalyticsApi } from "@/lib/api/analytics-api";
import { useUnseenNotificationsQuery } from "@/lib/queries/notification-queries";

type SidebarMenuItem = {
  label: string;
  icon: React.ReactNode;
  href: string;
};

export default function AppShell() {
  const navigate = useNavigate();
  const { teamExternalId } = useParams();
  const [isAddCreditsDialogOpen, setIsAddCreditsDialogOpen] = useState(false);
  const { data: self } = useSelfQuery();
  const { data: team } = useTeamQuery(teamExternalId!);

  if (!teamExternalId || !team || !self) {
    console.error("No team external ID or self found");
    return null;
  }

  return (
    <SidebarProvider>
      <Sidebar>
        <SidebarHeader>
          <Link to={paths.home(teamExternalId)}>
            <img
              src="/AmplifilesLogo2.png"
              alt="Amplifiles Logo"
              className="h-14 w-14"
            />
          </Link>
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <HomeLink teamExternalId={teamExternalId} />
                <VideosLink teamExternalId={teamExternalId} />
                {team.isDfyEditorTeam ? (
                  <OrdersLink teamExternalId={teamExternalId} />
                ) : (
                  <DfyLink teamExternalId={teamExternalId} />
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter>
          <SidebarGroup className="mb-2">
            <SidebarGroupContent>
              <SidebarMenu>
                <TeamsLink teamExternalId={teamExternalId} />
                <BillingLink teamExternalId={teamExternalId} />
                <FeatureRequestLink teamExternalId={teamExternalId} />
                <AdminDashboardLink />
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
          <CreditBalanceSidebar
            onAddCreditsClick={() => setIsAddCreditsDialogOpen(true)}
            creditBalance={team.billingInfo.creditBalance}
            showCredits={team.billingInfo.showCredits}
          />
          <TeamSwitcher
            classNameTrigger="px-4 py-5 hover:bg-accent"
            isUserVisible
            teamExternalId={teamExternalId}
            onTeamSwitch={(newTeamExternalId) => {
              navigate(paths.home(newTeamExternalId));
            }}
          />
        </SidebarFooter>
      </Sidebar>
      <div className="flex flex-1 flex-col self-stretch overflow-hidden">
        <Outlet />
      </div>
      <AddCreditsDialog
        teamExternalId={teamExternalId!}
        isOpen={isAddCreditsDialogOpen}
        setIsOpen={setIsAddCreditsDialogOpen}
        team={team}
      />
      <OnboardingSurveyDialog />
    </SidebarProvider>
  );
}

function HomeLink({ teamExternalId }: { teamExternalId: string }) {
  const location = useLocation();

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={
          location.pathname.startsWith(paths.home(teamExternalId)) ||
          location.pathname.startsWith(paths.tutorials(teamExternalId))
        }
      >
        <Link to={paths.home(teamExternalId)}>
          <IconHome className="h-5 w-5" />
          <span>Home</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function VideosLink({ teamExternalId }: { teamExternalId: string }) {
  const location = useLocation();

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={location.pathname.startsWith(paths.videos(teamExternalId))}
      >
        <Link to={paths.videos(teamExternalId)}>
          <IconVideo className="h-5 w-5" />
          <span>Videos</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function DfyLink({ teamExternalId }: { teamExternalId: string }) {
  const location = useLocation();
  const { data: unseen = [] } = useUnseenNotificationsQuery();
  const hasUnseenOrderNotification = unseen.some(
    (notification) =>
      notification.type === "DFY_ORDER_DELIVERED" &&
      notification.dfyOrder?.teamExternalId === teamExternalId,
  );
  const handleClick = () => {
    AnalyticsApi.logDfyLinkClicked(teamExternalId);
  };

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={location.pathname.startsWith(paths.dfy(teamExternalId))}
      >
        <Link to={paths.dfy(teamExternalId)} onClick={handleClick}>
          <IconPackage className="h-5 w-5" />
          <span className="flex-1">Done-for-You</span>
          {hasUnseenOrderNotification && (
            <span className="h-2.5 w-2.5 shrink-0 rounded-full border border-white bg-linear-to-br from-primary-400 to-secondary-400" />
          )}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function OrdersLink({ teamExternalId }: { teamExternalId: string }) {
  const location = useLocation();

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={location.pathname.startsWith(
          paths.dfyEditorOrders(teamExternalId),
        )}
      >
        <Link to={paths.dfyEditorOrders(teamExternalId)}>
          <IconClipboardList className="h-5 w-5" />
          <span>Orders</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function TeamsLink({ teamExternalId }: { teamExternalId: string }) {
  const location = useLocation();

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={location.pathname.startsWith(paths.team(teamExternalId))}
      >
        <Link to={paths.team(teamExternalId)}>
          <IconUsers className="h-5 w-5" />
          <span>Team</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function BillingLink({ teamExternalId }: { teamExternalId: string }) {
  const { data: team } = useTeamQuery(teamExternalId);
  const teamMembership = useCurrentTeamMembership();

  const { mutate: openCustomerPortalUrl, isPending } =
    useGenerateCustomerPortalUrl({
      teamExternalId: teamExternalId,
      onSuccess: (data) => {
        window.location.href = data.url;
      },
      onError: (error) => {
        console.error(error);
        Toast.error("Failed to open billing portal");
      },
    });

  if (
    !teamMembership?.permissions.includes(TeamPermissions.BILLING_EDIT) ||
    !team ||
    !team.billingInfo.showCredits
  ) {
    return null;
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton onClick={() => openCustomerPortalUrl()}>
        <IconCoin className="h-5 w-5" />
        <span className="flex-1">Billing</span>
        {isPending && <LoadingSpinner className="h-4 w-4" />}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function AdminDashboardLink() {
  const isFeatureEnabled = useIsFeatureEnabled();

  if (!isFeatureEnabled) {
    return null;
  }

  const handleAdminClick = () => {
    const adminUrl = getEnv("VITE_AMPLIFILES_ADMIN_URL");
    window.open(adminUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <SidebarMenuItem>
      <SidebarMenuButton onClick={handleAdminClick}>
        <IconShieldLock className="h-5 w-5" />
        <span>Admin Dashboard</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function FeatureRequestLink({ teamExternalId }: { teamExternalId: string }) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild>
        <Link
          target="_blank"
          to={externalPaths.featureRequests()}
          onClick={() =>
            AnalyticsApi.logFeatureRequestsLinkClicked(teamExternalId)
          }
        >
          <IconBulb className="h-5 w-5" />
          <span>Feature Requests</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}
